/* eslint-disable max-lines */
// Dialog for removing items from a shipment — removed items are split into a new separate shipment (POM-5103)
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MedicationIcon from '@mui/icons-material/Medication';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LocalPharmacyOutlinedIcon from '@mui/icons-material/LocalPharmacyOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import UndoIcon from '@mui/icons-material/Undo';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import {
  cancelShipmentItemsForReviewApi,
  cancelShipmentItemsFromOrderApi,
  splitShipmentItemsApi,
} from 'api/shipment';
import { closeSnackbar, useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

const COLS = '80px 1fr 160px 55px 90px';
const roSx = { fontSize: '0.8rem' };
const roMonoSx = { fontFamily: 'monospace', fontSize: '0.8rem' };
const iconCircleSx = (color) => ({
  width: 32,
  height: 32,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: color,
  flexShrink: 0,
});

const RemoveShipmentItemsDialog = ({ open, onClose, onSuccess, shipment, shipmentItems }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [localItems, setLocalItems] = useState([]);
  const [removedIds, setRemovedIds] = useState(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removalOption, setRemovalOption] = useState('new_shipment');

  useEffect(() => {
    if (open) {
      setLocalItems(shipmentItems || []);
      setRemovedIds(new Set());
      setRemovalOption('new_shipment');
    }
  }, [open, shipmentItems]);

  const removedItems = localItems.filter((i) => removedIds.has(i.lineItemId));
  const activeItems = localItems.filter((i) => !removedIds.has(i.lineItemId));

  const handleRemoveRow = (item) => {
    setRemovedIds((prev) => new Set([...prev, item.lineItemId]));
  };

  const handleUndoRow = (item) => {
    setRemovedIds((prev) => {
      const n = new Set(prev);
      n.delete(item.lineItemId);
      return n;
    });
  };

  const handleSave = () => {
    if (removedItems.length === 0) {
      onClose();
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    if (saving) return;
    setConfirmOpen(false);
  };

  const handleConfirmSave = async () => {
    setSaving(true);
    try {
      const itemReferenceIds = removedItems.map((i) => i.lineItemId);
      const count = removedItems.length;

      if (removalOption === 'new_shipment') {
        const result = await splitShipmentItemsApi({
          shipmentId: shipment.shipmentId,
          referenceId: shipment.referenceId,
          itemReferenceIds,
        });
        const { newShipment } = result ?? {};
        enqueueSnackbar(
          `${count} item${count > 1 ? 's' : ''} moved to a new shipment.${
            newShipment?.referenceId ? ` New shipment: ${newShipment.referenceId}` : ''
          }`,
          {
            variant: 'success',
            action: (snackbarId) => (
              <>
                <IconButton
                  size='small'
                  color='inherit'
                  title='Copy reference ID'
                  onClick={() => navigator.clipboard.writeText(newShipment?.referenceId ?? '')}
                >
                  <ContentCopyIcon fontSize='small' />
                </IconButton>
                <IconButton
                  size='small'
                  color='inherit'
                  aria-label='close'
                  onClick={() => closeSnackbar(snackbarId)}
                >
                  <CloseIcon fontSize='small' />
                </IconButton>
              </>
            ),
          }
        );
      } else if (removalOption === 'cancel_shipment') {
        await cancelShipmentItemsForReviewApi({
          shipmentId: shipment.shipmentId,
          referenceId: shipment.referenceId,
          itemReferenceIds,
        });
        enqueueSnackbar(`${count} item${count > 1 ? 's' : ''} routed to Ship Later review.`, {
          variant: 'success',
        });
      } else if (removalOption === 'cancel_item') {
        await cancelShipmentItemsFromOrderApi({
          shipmentId: shipment.shipmentId,
          referenceId: shipment.referenceId,
          itemReferenceIds,
        });
        enqueueSnackbar(`${count} item${count > 1 ? 's' : ''} cancelled from order.`, {
          variant: 'warning',
        });
      }

      setConfirmOpen(false);
      onSuccess(removedItems.map((i) => i.lineItemId));
    } catch {
      enqueueSnackbar('Failed to remove items. Please try again.', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  const canRemoveMore = activeItems.length > 1;

  const CONFIRM_TITLES = {
    cancel_item: 'Confirm Cancellation from Order',
    cancel_shipment: 'Confirm Route to Ship Later',
    new_shipment: 'Confirm Move to New Shipment',
  };
  const confirmTitle = CONFIRM_TITLES[removalOption] || CONFIRM_TITLES.new_shipment;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
        <DialogTitle sx={{ pb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant='h6' fontWeight={600}>
              Edit Shipment Items
            </Typography>
            <IconButton size='small' onClick={onClose}>
              <CloseIcon fontSize='small' />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 0 }}>
          {/* Primary header — brand navy */}
          <Box sx={{ borderRadius: 2, overflow: 'hidden', mb: 1.5, bgcolor: '#1a3a6b' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, px: 2.5, py: 1.75 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={iconCircleSx('rgba(255,255,255,0.12)')}>
                  <Inventory2OutlinedIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.9)' }} />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: '0.62rem',
                      color: 'rgba(255,255,255,0.55)',
                      textTransform: 'uppercase',
                      letterSpacing: 0.8,
                    }}
                  >
                    Order
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.9rem',
                      fontWeight: 700,
                      color: '#fff',
                      fontFamily: 'monospace',
                      letterSpacing: 0.5,
                    }}
                  >
                    {shipment.referenceId}
                  </Typography>
                </Box>
              </Box>
              <Divider
                orientation='vertical'
                flexItem
                sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: 0.5 }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                <Box sx={iconCircleSx('rgba(255,255,255,0.12)')}>
                  <PersonOutlineIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.9)' }} />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: '0.62rem',
                      color: 'rgba(255,255,255,0.55)',
                      textTransform: 'uppercase',
                      letterSpacing: 0.8,
                    }}
                  >
                    Recipient
                  </Typography>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#fff' }}>
                    {shipment.recipientName}
                  </Typography>
                </Box>
              </Box>
              <Chip
                label={`${shipment.totalItems ?? localItems.length} Items`}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  height: 26,
                  borderRadius: 2,
                  border: '1px solid rgba(255,255,255,0.3)',
                }}
              />
            </Box>
          </Box>

          {/* Secondary row — Partner / Practice / Pharmacy with icons */}
          <Box
            sx={{
              display: 'flex',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              mb: 2,
              overflow: 'hidden',
            }}
          >
            {[
              {
                label: 'Partner',
                value: shipment.partnerName,
                Icon: PeopleOutlineIcon,
                color: '#e8ecf5',
                iconColor: '#031c57',
              },
              {
                label: 'Practice',
                value: shipment.practiceName,
                Icon: BusinessIcon,
                color: '#e8ecf5',
                iconColor: '#031c57',
              },
              {
                label: 'Pharmacy',
                value: shipment.fulfillingPharmacyName,
                Icon: LocalPharmacyOutlinedIcon,
                color: '#e8ecf5',
                iconColor: '#031c57',
              },
            ].map(({ label, value, Icon, color, iconColor }, i) =>
              value ? (
                <Box
                  key={label}
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2,
                    py: 1.25,
                    ...(i > 0 && { borderLeft: '1px solid', borderColor: 'divider' }),
                  }}
                >
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: color,
                      flexShrink: 0,
                    }}
                  >
                    <Icon sx={{ fontSize: 15, color: iconColor }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: '0.62rem',
                        color: 'text.disabled',
                        textTransform: 'uppercase',
                        letterSpacing: 0.6,
                        fontWeight: 600,
                      }}
                    >
                      {label}
                    </Typography>
                    <Typography variant='body2' fontWeight={500} noWrap>
                      {value}
                    </Typography>
                  </Box>
                </Box>
              ) : null
            )}
          </Box>

          {/* Items table */}
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: COLS,
                gap: 1.5,
                px: 2,
                py: 1,
                bgcolor: 'grey.50',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              {['NDC', 'Drug Name', 'Order ID', 'Qty', 'Actions'].map((col) => (
                <Typography
                  key={col}
                  variant='caption'
                  fontWeight={700}
                  color='text.secondary'
                  sx={{ textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: 0.4 }}
                >
                  {col}
                </Typography>
              ))}
            </Box>

            {/* Rows */}
            <Box sx={{ minHeight: 260, maxHeight: 300, overflowY: 'auto', position: 'relative' }}>
              {saving && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(255,255,255,0.85)',
                    zIndex: 1,
                    gap: 1.5,
                  }}
                >
                  <CircularProgress size={32} />
                  <Typography variant='body2' color='text.secondary'>
                    Processing…
                  </Typography>
                </Box>
              )}
              {!saving && activeItems.length === 0 && removedItems.length === 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 120,
                  }}
                >
                  <Typography variant='body2' color='text.disabled'>
                    All items marked for removal. Click Save Changes to confirm.
                  </Typography>
                </Box>
              )}
              {localItems.map((item, idx) => {
                const isRemoved = removedIds.has(item.lineItemId);
                const subtitle = [item.strength, item.form].filter(Boolean).join(' • ');
                return (
                  <Box
                    key={item.lineItemId}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: COLS,
                      alignItems: 'center',
                      gap: 1.5,
                      px: 2,
                      py: 1,
                      borderBottom: idx < localItems.length - 1 ? '1px solid' : 'none',
                      borderColor: 'grey.100',
                      bgcolor: isRemoved ? 'grey.50' : 'transparent',
                      transition: 'background-color 0.2s',
                    }}
                  >
                    <Typography
                      sx={{
                        ...roMonoSx,
                        color: 'text.secondary',
                        textDecoration: isRemoved ? 'line-through' : 'none',
                      }}
                    >
                      {item.sku || '-'}
                    </Typography>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        noWrap
                        sx={{
                          ...roSx,
                          fontWeight: 500,
                          textDecoration: isRemoved ? 'line-through' : 'none',
                          color: isRemoved ? 'text.disabled' : 'text.primary',
                        }}
                      >
                        {item.name || '-'}
                      </Typography>
                      {subtitle && (
                        <Typography
                          noWrap
                          sx={{
                            fontSize: '0.7rem',
                            color: 'text.secondary',
                            textDecoration: isRemoved ? 'line-through' : 'none',
                          }}
                        >
                          {subtitle}
                        </Typography>
                      )}
                    </Box>
                    <Typography
                      sx={{
                        ...roMonoSx,
                        textDecoration: isRemoved ? 'line-through' : 'none',
                        color: isRemoved ? 'text.disabled' : 'text.primary',
                      }}
                    >
                      {item.orderId || '-'}
                    </Typography>
                    <Typography
                      sx={{
                        ...roSx,
                        fontWeight: 500,
                        textDecoration: isRemoved ? 'line-through' : 'none',
                        color: isRemoved ? 'text.disabled' : 'text.primary',
                      }}
                    >
                      {item.quantity ?? '-'}
                    </Typography>
                    {isRemoved ? (
                      <IconButton
                        size='small'
                        disableRipple
                        onClick={() => handleUndoRow(item)}
                        sx={{
                          color: 'warning.main',
                          '&:hover': { bgcolor: 'warning.50' },
                          borderRadius: 1,
                        }}
                      >
                        <UndoIcon fontSize='small' />
                      </IconButton>
                    ) : (
                      <IconButton
                        size='small'
                        disableRipple
                        disabled={!canRemoveMore}
                        onClick={() => handleRemoveRow(item)}
                        sx={{
                          color: 'error.main',
                          '&:hover': { bgcolor: 'error.50' },
                          borderRadius: 1,
                          '&.Mui-disabled': { color: 'action.disabled' },
                        }}
                      >
                        <DeleteIcon fontSize='small' />
                      </IconButton>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{ px: 3, py: 2, flexDirection: 'column', alignItems: 'stretch', gap: 0.75 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Status chip */}
            {removedItems.length === 0 ? (
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
                label='No changes yet'
                size='small'
                sx={{
                  bgcolor: '#f0fdf4',
                  color: '#16a34a',
                  fontWeight: 600,
                  fontSize: '0.72rem',
                  border: '1px solid #bbf7d0',
                  height: 26,
                }}
              />
            ) : (
              <Chip
                label={`${removedItems.length} item${removedItems.length !== 1 ? 's' : ''} removed`}
                size='small'
                color='error'
                sx={{ fontWeight: 600, fontSize: '0.72rem', height: 26 }}
              />
            )}
            <Box sx={{ display: 'flex', gap: 2, ml: 1 }}>
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  Removed
                </Typography>
                <Typography
                  variant='body2'
                  fontWeight={700}
                  color={removedItems.length > 0 ? 'error.main' : 'text.primary'}
                >
                  {removedItems.length} item{removedItems.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  Total Qty
                </Typography>
                <Typography variant='body2' fontWeight={700}>
                  {removedItems.reduce((s, i) => s + (i.quantity || 0), 0)}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ flex: 1 }} />
            <Button
              variant='outlined'
              onClick={onClose}
              sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              variant='contained'
              disabled={removedItems.length === 0}
              onClick={handleSave}
              sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}
            >
              Save Changes
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmOpen}
        onClose={handleConfirmClose}
        maxWidth='sm'
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, maxHeight: '85vh' } }}
      >
        <DialogTitle sx={{ px: 3, pt: 3, pb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, #fff3e0 40%, #fef9f0 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <WarningAmberIcon sx={{ color: '#f97316', fontSize: 32 }} />
              </Box>
              <Box>
                <Typography
                  variant='h6'
                  fontWeight={700}
                  sx={{ fontSize: '1.05rem', lineHeight: 1.3, mb: 0.75 }}
                >
                  {confirmTitle}
                </Typography>
                <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.6 }}>
                  {removedItems.length} item{removedItems.length !== 1 ? 's' : ''} selected. Choose
                  what to do with the removed item{removedItems.length !== 1 ? 's' : ''} below.
                </Typography>
              </Box>
            </Box>
            <IconButton size='small' onClick={handleConfirmClose} disabled={saving}>
              <CloseIcon fontSize='small' />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 0, pb: 2 }}>
          {/* Removal option selector */}
          <RadioGroup
            value={removalOption}
            onChange={(e) => setRemovalOption(e.target.value)}
            sx={{ mb: 2 }}
          >
            <FormControlLabel
              value='new_shipment'
              control={<Radio />}
              label='Move items to a new shipment'
            />
            <FormControlLabel
              value='cancel_shipment'
              control={<Radio />}
              label='Cancel Shipment item(s) and Route to Ship Later review (if POM Order exists)'
            />
            <FormControlLabel
              value='cancel_item'
              control={<Radio />}
              label='Cancel Shipment item(s) and Cancel item(s) from order (if POM Order exists)'
            />
          </RadioGroup>

          {/* Info box */}
          <Box
            sx={{ display: 'flex', gap: 1.5, p: 2, mb: 2.5, bgcolor: '#eef2ff', borderRadius: 1.5 }}
          >
            <InfoOutlinedIcon sx={{ color: '#1a3a6b', fontSize: 18, mt: 0.2, flexShrink: 0 }} />
            <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.6 }}>
              You can review all changes before saving. No changes will be saved until you click{' '}
              <Typography
                component='span'
                variant='body2'
                fontWeight={700}
                sx={{ color: '#1a3a6b' }}
              >
                &ldquo;Remove Items&rdquo;
              </Typography>
              .
            </Typography>
          </Box>

          {/* Items list */}
          <Typography
            variant='caption'
            fontWeight={700}
            color='text.secondary'
            sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5, display: 'block' }}
          >
            Items to be removed ({removedItems.length})
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              maxHeight: 220,
              overflowY: 'auto',
              pr: 0.5,
            }}
          >
            {removedItems.map((item) => (
              <Box
                key={item.lineItemId}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  px: 2,
                  py: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: '50%',
                    bgcolor: '#eef2ff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <MedicationIcon sx={{ fontSize: 19, color: '#1a3a6b' }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant='body2' fontWeight={600} noWrap>
                    {item.name || '-'}
                  </Typography>
                  {item.orderId && (
                    <Typography variant='caption' color='text.secondary'>
                      Order: {item.orderId}
                    </Typography>
                  )}
                </Box>
                <Typography
                  variant='body2'
                  color='text.secondary'
                  fontWeight={500}
                  sx={{ flexShrink: 0 }}
                >
                  Qty: {item.quantity ?? '-'}
                </Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pt: 1, pb: 3, gap: 1.5 }}>
          <Button
            variant='outlined'
            onClick={handleConfirmClose}
            disabled={saving}
            sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <LoadingButton
            variant='contained'
            loading={saving}
            onClick={handleConfirmSave}
            startIcon={!saving && <DeleteIcon fontSize='small' />}
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#1a3a6b',
              '&:hover': { bgcolor: '#031c57' },
            }}
          >
            Remove Items
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

RemoveShipmentItemsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  shipment: PropTypes.shape({
    shipmentId: PropTypes.string,
    referenceId: PropTypes.string,
    recipientName: PropTypes.string,
    partnerName: PropTypes.string,
    practiceName: PropTypes.string,
    fulfillingPharmacyName: PropTypes.string,
    totalItems: PropTypes.number,
  }).isRequired,
  shipmentItems: PropTypes.arrayOf(
    PropTypes.shape({
      lineItemId: PropTypes.string,
      name: PropTypes.string,
      sku: PropTypes.string,
      orderId: PropTypes.string,
      quantity: PropTypes.number,
    })
  ),
};

RemoveShipmentItemsDialog.defaultProps = {
  shipmentItems: [],
};

export default RemoveShipmentItemsDialog;
